// Phase E — single source of truth for image-generation-error copy, mirroring
// the pattern config/enforcementPresentation.js already established: the
// backend computes a semantic `presentationKey` (who-would-win-server's
// errors/imageGenerationPresentation.js), this file only owns how it reads.
// Never derive a presentationKey from anything on the client — always read
// it straight from the server response/SSE event.
//
// Every entry carries every presentation context as data, never assembled
// in a component:
//   title / detail        — normal failure, no canonical image, no partial retained
//   previewDetail          — a degraded partial is being shown instead (previewEligible)
//   restoredTitle / restoredDetail — a failed regeneration left a prior
//     canonical image in place; the component shows that image plus this
//     compact notice instead of an error panel
// `title`/`detail` are always present; `previewDetail` only exists for the
// 2 keys that can carry a retained partial; `restoredTitle`/`restoredDetail`
// exist for every key that can realistically arise mid-regeneration.
export const IMAGE_ERROR_PRESENTATION = {
  IMAGE_PROVIDER_OUTPUT_BLOCK: {
    title: "Image generation stopped",
    detail: "A safety check stopped the image before it could complete. This does not mean your matchup violated our Rules.",
    restoredTitle: "New image couldn't be generated",
    restoredDetail: "Your new image was stopped by a safety check. Your previous image has been restored.",
  },
  IMAGE_PROVIDER_INPUT_BLOCK: {
    title: "Image couldn't be generated",
    detail: "A safety check blocked the image request before an image was generated.",
    restoredTitle: "New image couldn't be generated",
    restoredDetail: "Your new image request was blocked by a safety check. Your previous image has been restored.",
  },
  IMAGE_PROVIDER_BLOCK_UNKNOWN_STAGE: {
    title: "Image couldn't be generated",
    detail: "A safety check prevented this image from completing. This does not mean your matchup violated our Rules.",
    restoredTitle: "New image couldn't be generated",
    restoredDetail: "Your new image was stopped by a safety check. Your previous image has been restored.",
  },
  IMAGE_FIRST_PARTY_INPUT_BLOCK: {
    title: "Image couldn't be generated",
    detail: "The image request didn't pass a safety check.",
    // Failure happens before beginRegeneration() ever touches state, so the
    // previous canonical image was never actually removed — "still
    // available," not "restored."
    restoredTitle: "New image couldn't be generated",
    restoredDetail: "The new image request didn't pass a safety check. Your previous image is still available.",
  },
  IMAGE_LEGACY_BLOCK: {
    title: "Image unavailable",
    detail: "A safety check prevented the image from being available.",
    restoredTitle: "New image couldn't be generated",
    restoredDetail: "A safety check prevented the new image from being available. Your previous image has been restored.",
  },
  IMAGE_TECHNICAL_INTERRUPTED: {
    title: "Image generation was interrupted",
    detail: "Something interrupted image generation. Please try again.",
    previewDetail: "Image generation was interrupted. Showing the latest preview.",
    restoredTitle: "Regeneration was interrupted",
    restoredDetail: "Regeneration was interrupted. Your previous image has been restored.",
  },
  IMAGE_INCOMPLETE_STREAM: {
    title: "Image generation was interrupted",
    detail: "Something interrupted image generation. Please try again.",
    previewDetail: "Image generation was interrupted. Showing the latest preview.",
    restoredTitle: "Regeneration was interrupted",
    restoredDetail: "Regeneration was interrupted. Your previous image has been restored.",
  },
  IMAGE_INCOMPLETE_PROVIDER_RESULT: {
    title: "Image couldn't be generated",
    detail: "Something prevented this image from completing. Please try again later.",
    restoredTitle: "Regeneration didn't complete",
    restoredDetail: "The new image didn't complete. Your previous image has been restored.",
  },
  IMAGE_RATE_LIMITED: {
    title: "Image generation is busy",
    detail: "Image generation is temporarily unavailable. Please try again shortly.",
    restoredTitle: "Regeneration is busy",
    restoredDetail: "Image generation is temporarily unavailable. Your previous image has been restored.",
  },
  IMAGE_TEMPORARILY_UNAVAILABLE: {
    title: "Image generation is temporarily unavailable",
    detail: "We couldn't start image generation right now.",
    restoredTitle: "Regeneration is temporarily unavailable",
    restoredDetail: "We couldn't start a new image right now. Your previous image has been restored.",
  },
  IMAGE_INVALID_REQUEST: {
    title: "Image couldn't be generated",
    detail: "The image request couldn't be processed.",
    restoredTitle: "Regeneration couldn't be processed",
    restoredDetail: "The new image request couldn't be processed. Your previous image has been restored.",
  },
  IMAGE_UNKNOWN_FAILURE: {
    title: "Image couldn't be generated",
    detail: "Something prevented this image from completing. Please try again later.",
    restoredTitle: "Regeneration didn't complete",
    restoredDetail: "Something prevented the new image from completing. Your previous image has been restored.",
  },
  IMAGE_MODERATION_UNAVAILABLE: {
    title: "Image generation is temporarily unavailable",
    detail: "We couldn't complete the safety check needed to start image generation. Please try again shortly.",
    // Same pre-beginRegeneration() timing as IMAGE_FIRST_PARTY_INPUT_BLOCK —
    // the previous canonical image was never touched.
    restoredTitle: "Image generation is temporarily unavailable",
    restoredDetail: "We couldn't complete the safety check needed to start a new image. Your previous image is still available.",
  },
};

// Malformed/unrecognized/missing presentationKey never crashes — falls back
// to the same generic entry the old getErrorMessage(errCode) defaulted to.
export function getImageErrorPresentation(presentationKey) {
  return IMAGE_ERROR_PRESENTATION[presentationKey] ?? IMAGE_ERROR_PRESENTATION.IMAGE_UNKNOWN_FAILURE;
}

// Picks which {title, detail} pair applies for the current rendering
// context — the only place that decision is made; components never
// concatenate or select copy fragments themselves.
//   hasCanonical: a valid previous image exists and is being shown/restored
//     (takes priority — the user always sees the best available media, with
//     the failure reason as a compact secondary notice, never suppressed).
//   previewEligible: no canonical image, but a retained partial is shown.
export function pickImageErrorContext(presentationKey, { hasCanonical = false, previewEligible = false } = {}) {
  const entry = getImageErrorPresentation(presentationKey);
  if (hasCanonical) {
    return { title: entry.restoredTitle ?? entry.title, detail: entry.restoredDetail ?? entry.detail };
  }
  if (previewEligible) {
    return { title: entry.title, detail: entry.previewDetail ?? entry.detail };
  }
  return { title: entry.title, detail: entry.detail };
}

// Deterministic, allowlisted category labels — mirrors the backend's fixed
// safeCategoryDetails() order (violence, sexual, harassment, self-harm).
// Any category the backend didn't recognize was already dropped server-side;
// this only ever renders what arrives.
const CATEGORY_LABELS = {
  violence: "Violence",
  sexual: "Sexual content",
  harassment: "Harassment",
  "self-harm": "Self-harm",
};

// "Safety category reported: X" for one, "Safety categories reported: X, Y"
// for 2+. Returns null when there's nothing to show (no secondary line
// rendered at all) rather than an empty string.
export function formatCategoryDetailsLine(categoryDetails) {
  if (!Array.isArray(categoryDetails) || categoryDetails.length === 0) return null;
  const labels = categoryDetails.map((c) => CATEGORY_LABELS[c]).filter(Boolean);
  if (labels.length === 0) return null;
  const noun = labels.length === 1 ? "category" : "categories";
  return `Safety ${noun} reported: ${labels.join(", ")}`;
}
